/* FORMÁT ČÍSLA A DATA PODLE JAZYKA — jedno místo pro celou aplikaci.
 * ============================================================================
 *
 * PROČ TENHLE SOUBOR VZNIKL (vizuální QA 20. 8. 2026, nálezy N1 a N7)
 * -------------------------------------------------------------------
 *
 * 1) DATUM V AMERICKÉM POŘADÍ. Nástěnka řezala ISO řetězec:
 *
 *        (tk.deadline || "").slice(5, 10).replace("-", ".")
 *        "2027-06-01"  →  "06.01"
 *
 *    Čech to přečte jako „6. ledna". Ve skutečnosti je to 1. června 2027.
 *    A protože se rok uřízl, vypadala událost za rok a půl jako „za pár dní".
 *    Řezání řetězce datum NEFORMÁTUJE — jen ho zkracuje na místě, kde má
 *    každý jazyk jiné pořadí složek.
 *
 * 2) DESETINNÁ TEČKA. `value.toFixed(2)` vrací VŽDY americký zápis „3.50",
 *    protože to je JS literál čísla, ne formát pro čtenáře. Klient na
 *    /fin-matematika viděl „3.50 %" a „30.0 let".
 *
 * ⚠️ TEČKA NENÍ CHYBA VŠUDE. V angličtině je „3.50" správně a čárka by byla
 *    chyba. Proto se tu nikde nedělá `.replace(".", ",")` — o oddělovači
 *    rozhoduje `Intl`, podle locale.
 *
 * ⚠️ TENHLE SOUBOR JE JEN O ZOBRAZENÍ. Do výpočtu nikdy nevstupuje jeho
 *    výstup — vrací řetězce pro člověka. Číslo do enginu si nechte číslem.
 *
 * ⚠️ MĚNA SEM NEPATŘÍ. Symbol jde ze ZEMĚ TENANTA (`lib/tenantMena.js`),
 *    nikdy z jazyka — viz `docs/MENA-A-JAZYK.md`. Jazyk rozhoduje jen
 *    o oddělovačích a o pořadí den/měsíc/rok.
 */

import { asCislo } from "@/lib/bezpecneHodnoty";

/* LOCALE PODLE JAZYKA — ZDROJ PRAVDY PRO CELOU APLIKACI.
 *
 * Tahle tabulka bydlela v `contexts/LanguageContext.js` a používala ji jen
 * `currency.locale`. Teď z ní čte i formátování čísel a dat, takže je
 * schválně tady (čistý modul bez Reactu) a context ji importuje. Dvě kopie
 * by se rozešly a částka v grafu by měla jiný oddělovač než popisek pod ním.
 *
 * ⚠️ `en`, `uk` a `vi` míří ZÁMĚRNĚ na `cs-CZ` — je to původní rozhodnutí
 * z LanguageContextu a nemění se: Ukrajinec ani Vietnamec pracující v Česku
 * není zahraniční tenant, chce appku ve své řeči, ale české formátování
 * (a české koruny, ty mu ale dá tenant, ne tahle tabulka). U `en` to znamená,
 * že uvidí „3,50" a „1. 6. 2027“.
 *
 * KDYBY MĚLA MÍT ANGLIČTINA ANGLICKÝ FORMÁT, je to změna jednoho řádku tady
 * (`en: "en-GB"`) — ale projeví se i na VŠECH částkách v aplikaci, protože
 * `currency.locale` čte tutéž tabulku. Proto to není tichá změna a nemá se
 * dělat bez rozhodnutí majitele.
 */
export const LOCALE_JAZYKA = {
  cs: "cs-CZ",
  sk: "sk-SK",
  en: "cs-CZ",
  uk: "cs-CZ",
  vi: "cs-CZ",
};

/** Když jazyk neznáme (nebo přijde `undefined`), sázíme česky. */
export const VYCHOZI_LOCALE = "cs-CZ";

/** Kód jazyka („cs", „sk"…) → BCP-47 locale pro `Intl`. */
export function localeJazyka(lang) {
  return LOCALE_JAZYKA[String(lang || "").toLowerCase()] || VYCHOZI_LOCALE;
}

/* ═══════════════════════════════════════════════════════════ ČÍSLA ══ */

/** Co se vysází místo čísla, které neexistuje. Nikdy ne „0" — nula je
 *  tvrzení („klient nemá nic"), pomlčka je přiznání („nevíme"). */
export const NEZNAMO = "—";

/* PROČ NE `Number(x)`: `Number(null)`, `Number("")` i `Number(false)` vrací 0.
 * Formátovač by z chybějícího údaje udělal „0,00 %" — tedy tvrzení o klientovi,
 * které nikdo nezadal. `asCislo` z lib/bezpecneHodnoty.js drží v celém repu
 * pravidlo „prázdno není nula"; sdílí se schválně, ať se ta hranice neposune
 * jen tady. (Test `format.test.js` na tohle padal — bez něj by prošlo „0,00".) */

/**
 * Desetinné číslo pro čtenáře. Nahrazuje `value.toFixed(mist)` v UI.
 *
 *   desetinne(3.5, 2, "cs")   → „3,50"
 *   desetinne(3.5, 2, "en")   → „3,50"  (viz LOCALE_JAZYKA — en míří na cs-CZ)
 *   desetinne(30, 1, "sk")    → „30,0"
 *   desetinne(Infinity, 1)    → „—"
 *
 * Počet míst je PEVNÝ (min i max), aby „30" nevypadalo jinak než „30,5" —
 * v tabulce vedle sebe je kolísavý počet desetinných míst hůř čitelný než
 * jedna nula navíc.
 *
 * @param {number} hodnota
 * @param {number} mist    počet desetinných míst (0 = celé číslo)
 * @param {string} lang    kód jazyka z useLanguage()
 */
export function desetinne(hodnota, mist = 2, lang) {
  const n = asCislo(hodnota);
  if (n === null) return NEZNAMO;
  const d = Math.max(0, Math.min(20, Math.trunc(Number(mist) || 0)));
  return new Intl.NumberFormat(localeJazyka(lang), {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n);
}

/**
 * Celé číslo s oddělovačem tisíců („3 000 000"). Nahrazuje
 * `Math.round(x).toLocaleString("cs-CZ")` tam, kde byl locale natvrdo.
 */
export function celeCislo(hodnota, lang) {
  const n = asCislo(hodnota);
  if (n === null) return NEZNAMO;
  return new Intl.NumberFormat(localeJazyka(lang), { maximumFractionDigits: 0 }).format(Math.round(n));
}

/* ═══════════════════════════════════════════════════════════ DATUM ══ */

/* Samotné datum („2027-06-01") se schválně skládá ručně, ne přes
 * `new Date(retezec)`. Prohlížeč čte ISO datum bez času jako UTC půlnoc,
 * takže v pásmu se záporným posunem vyjde o den dřív. `new Date(2027, 5, 1)`
 * je lokální půlnoc a den sedí všude. Datum S ČASEM se naopak parsuje
 * standardně — tam je posun významová informace, ne artefakt. */
const RE_JEN_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Cokoli, co v repu vystupuje jako datum, → `Date`, nebo `null`.
 * Přijímá `Date`, ISO řetězec s časem i holé „YYYY-MM-DD".
 */
export function naDatum(vstup) {
  if (vstup instanceof Date) return Number.isNaN(vstup.getTime()) ? null : vstup;
  if (typeof vstup === "number" && Number.isFinite(vstup)) {
    const d = new Date(vstup);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(vstup ?? "").trim();
  if (!s) return null;
  const m = RE_JEN_DATUM.exec(s);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * KRÁTKÉ DATUM DO SEZNAMU — s rokem jen tehdy, když není letošní.
 *
 *   kratkeDatum("2026-06-01", "cs")            → „1. 6."        (letos)
 *   kratkeDatum("2027-06-01", "cs")            → „1. 6. 2027"   (jindy)
 *   kratkeDatum("2027-06-01T09:00:00", "sk")   → „1. 6. 2027"
 *   kratkeDatum("", "cs")                      → „—"
 *
 * PROČ ROK PODMÍNĚNĚ: v hustém seznamu úkolů zabírá rok místo a u dnešního
 * týdne nic neříká. U termínu za rok a půl je naopak to jediné, co brání
 * poradci přečíst si ho jako „za pár dní" (přesně to je nález N1).
 *
 * @param {string|Date|number} vstup
 * @param {string} lang
 * @param {{dnes?: Date, prazdne?: string}} moznosti
 *        `dnes` je jen pro testy (žádná skrytá závislost na systémovém čase),
 *        `prazdne` mění výplň za neplatné datum (výchozí „—").
 */
export function kratkeDatum(vstup, lang, moznosti = {}) {
  const d = naDatum(vstup);
  if (!d) return moznosti.prazdne ?? NEZNAMO;
  const dnes = naDatum(moznosti.dnes) || new Date();
  const jinyRok = d.getFullYear() !== dnes.getFullYear();
  return d.toLocaleDateString(localeJazyka(lang), {
    day: "numeric",
    month: "numeric",
    ...(jinyRok ? { year: "numeric" } : null),
  });
}

/** Plné datum včetně roku — pro místa, kde se nesmí spoléhat na kontext. */
export function plneDatum(vstup, lang, moznosti = {}) {
  const d = naDatum(vstup);
  if (!d) return moznosti.prazdne ?? NEZNAMO;
  return d.toLocaleDateString(localeJazyka(lang), { day: "numeric", month: "numeric", year: "numeric" });
}
